import { roundCurrency, sumCurrency } from 'utils/currency';
import type { GrowlersCartRules } from 'utils/types/GrowlersCartRules';
import { DEFAULT_GROWLERS_CART_RULES } from 'utils/types/GrowlersCartRules';

export interface GrowlersCartPricing {
  subtotal: number;
  discount: number;
  discountPercent: number;
  shippingCost: number;
  total: number;
  quantityDiscountApplied: boolean;
  freeShippingApplied: boolean;
  itemsToDiscount: number;
  amountToFreeShipping: number;
}

const toNonNegative = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
};

export const normalizeGrowlersCartRules = (
  rules?: (Partial<GrowlersCartRules> & { discountAmount?: number }) | null,
): GrowlersCartRules => {
  const rawPercent = rules?.discountPercent ?? rules?.discountAmount;
  const discountPercent = Math.min(100, roundCurrency(toNonNegative(rawPercent)));

  return {
    discountMinItems: Math.floor(toNonNegative(rules?.discountMinItems)),
    discountPercent,
    shippingFee: roundCurrency(toNonNegative(rules?.shippingFee)),
    freeShippingMinValue: roundCurrency(
      toNonNegative(rules?.freeShippingMinValue),
    ),
  };
};

export const applyGrowlersCartRules = ({
  itemCount,
  subtotal,
  rules = DEFAULT_GROWLERS_CART_RULES,
}: {
  itemCount: number;
  subtotal: number;
  rules?: GrowlersCartRules | null;
}): GrowlersCartPricing => {
  const normalized = normalizeGrowlersCartRules(rules);
  const safeSubtotal = roundCurrency(Math.max(0, subtotal));
  const safeItemCount = Math.max(0, itemCount);

  const quantityDiscountApplied =
    normalized.discountMinItems > 0 &&
    normalized.discountPercent > 0 &&
    safeItemCount >= normalized.discountMinItems;

  const discount = quantityDiscountApplied
    ? roundCurrency((safeSubtotal * normalized.discountPercent) / 100)
    : 0;

  const subtotalAfterDiscount = roundCurrency(
    Math.max(0, safeSubtotal - discount),
  );

  const freeShippingApplied =
    normalized.freeShippingMinValue > 0 &&
    safeSubtotal >= normalized.freeShippingMinValue;

  const shippingCost = freeShippingApplied ? 0 : normalized.shippingFee;
  const total = sumCurrency(subtotalAfterDiscount, shippingCost);

  const itemsToDiscount =
    normalized.discountMinItems > 0 && !quantityDiscountApplied
      ? Math.max(0, normalized.discountMinItems - safeItemCount)
      : 0;

  const amountToFreeShipping =
    normalized.freeShippingMinValue > 0 && !freeShippingApplied
      ? roundCurrency(normalized.freeShippingMinValue - safeSubtotal)
      : 0;

  return {
    subtotal: safeSubtotal,
    discount,
    discountPercent: normalized.discountPercent,
    shippingCost,
    total,
    quantityDiscountApplied,
    freeShippingApplied,
    itemsToDiscount,
    amountToFreeShipping,
  };
};
